import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseComponent } from '../base-component';
import { AFAuthorization } from '../model/af.model';
import { AuthService } from '../service/auth.service';
import { EventService } from '../service/event.service';
import { ChangeNavigationEvent, ProgressBarEvent, ValidateAndNavigateEvent, SkipToMainContentEvent } from '../service/events';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DatePipe } from '@angular/common';
import { CustomValidatorsService } from '../service/custom-validators.service';

@Component({
  selector: 'app-fcra-authorization',
  templateUrl: './fcra-authorization.component.html',
  styleUrls: ['./fcra-authorization.component.scss'],
  standalone: false
})
export class FcraAuthorizationComponent extends BaseComponent implements OnInit, OnDestroy {

  authorizationUser: AFAuthorization;
  afId: number;
  signatureImg: string; // @Input wet-signature
  date: any;
  authData: UntypedFormGroup;
  fcraStatement: string;
  authLanguage: string;
  check: boolean = false;
  checkTwo: boolean = false;
  sigCheckValue: boolean = false;

  regexName: RegExp = /^[A-Za-z\u00C0-\u017F\-',. ]+$/;

  constructor(private readonly eventBus: EventService, private readonly _router: Router, private readonly fb: UntypedFormBuilder, private readonly userService: AuthService,
    private readonly _snackBar: MatSnackBar, private readonly datepipe: DatePipe, private readonly customValidator: CustomValidatorsService) {
    super(eventBus, _router);
  }

  get output() { return this.authData.get('output') };
  get authorizeConsent() { return this.authData.get('authorizeConsent') };
  get fcraLegalRequirementsMet() { return this.authData.get('fcraLegalRequirementsMet') };
  get fcraApplicantSignedName() { return this.authData.get('fcraApplicantSignedName') };
  get bookmarkUrl() { return this.authData.get('bookmarkUrl') };

  ngOnInit() {
    this.createGroup();
    this.subscribeEvents();
  }

  ngAfterViewInit() {
    this.authData.valueChanges.subscribe(() => {
      setTimeout(() => {
        let progress = this.getPercentage(this.authData);
        this.eventBus.emit(new ProgressBarEvent({ id: progress }));
      }, 100);
    });

    this.fcraLegalRequirementsMet.valueChanges.subscribe(() => {
      this.setDate();
    });

    // retain navigation on page refresh
    let navlink = this.userService.getNavLinks().find(e => e.routerLink === "/authorization");
    this.eventBus.emit(new ChangeNavigationEvent({ id: navlink.id }));
  }

  createGroup() {
    this.authData = this.fb.group({
      output: ['', Validators.required],
      authorizeConsent: ['', Validators.required],
      fcraLegalRequirementsMet: ['', Validators.required],
      bookmarkUrl: [''],
      fcraApplicantSignedName: ['', [Validators.required, this.customValidator.validateRequiredField, Validators.maxLength(250), Validators.pattern(this.regexName)]]
    });
  }

  subscribeEvents() {
    // Gets afAuthorization object
    this.userService.getAuthorizationDetails().subscribe({
      next: (data: AFAuthorization) => {
        this.authorizationUser = data;
        this.authData.patchValue(this.authorizationUser);
        this.afId = this.authorizationUser.afId;
        this.signatureImg = this.output.value;
        this.date = this.authorizationUser.fcraLegalRequirementCheckDate;
        this.fcraStatement = this.authorizationUser.languageLookup['FCRA_DISCLOSURE_9_9'];
        this.authLanguage = this.authorizationUser.authLanguageLookup;
      },
      error: (error: Error) => {
        console.log(error);
      }
    });

    this.sub = this.eventBus.on(
      ValidateAndNavigateEvent,
      (event): void => {
        if (event.payload.currentLink === '/authorization') {
          this.bookmarkUrl.setValue("");
          if (event.payload.save) {
            this.submitAuthorization((this.authData.value), event);
          } else if (event.payload.saveAndFinishLater) {
            this.bookmarkUrl.setValue(event.payload.currentLink);
            this.submitAuthorization((this.authData.value), event);
          } else if (this.validate()) {
            this.submitAuthorization((this.authData.value), event);
          }
        }
      }
    );

    this.eventBus.on(
      SkipToMainContentEvent,
      (event): void => {
        if (event.payload.text && event.payload.text == "/authorization") {
          let mainContentElement = document.querySelector("#main-content");//check if main-content id exists on page
          if (mainContentElement) {
            mainContentElement.scrollIntoView(true);
          }
        }
      }
    );
  }

  onFocusOutMainContent(event) {
    this.eventBus.emit(new SkipToMainContentEvent({ text: "FocusOff" }));
  }

  submitAuthorization(authorizationUser: AFAuthorization, event) {
    authorizationUser.output = this.signatureImg;
    authorizationUser.fcraLegalRequirementCheckDate = this.date;
    authorizationUser.authorizeConsentDate = this.date;
    authorizationUser.stateConsumerReportDate = this.date;
    this.userService.saveAuthorization(authorizationUser).subscribe({
      next: () => {
        if (event.payload.save) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
        } else if (event.payload.saveAndFinishLater) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
          this._router.navigate(['sessionTimedOut']);
        } else {
          this.eventBus.emit(new ChangeNavigationEvent({ id: event.payload.id }));
          this._router.navigateByUrl(event.payload.nextNavLink);
        }
      },
      error: (error: any) => console.log(error)
    });
  }

  getPercentage(container: UntypedFormGroup): number {
    let errorCount = 0;
    let totalCount = 0;

    for (let controlKey in container.controls) {

      if (container.controls.hasOwnProperty(controlKey)) {
        if (container.controls[controlKey].validator != null) {
          totalCount += container.controls[controlKey].validator.length;
        }
        if (container.controls[controlKey].errors) {
          errorCount += Object.keys(container.controls[controlKey].errors).length;
        }
      }
    }

    let percentage = ((totalCount - errorCount) / totalCount) * 100;
    return percentage;
  }

  getUrl(sig: string) {
    this.signatureImg = sig; // Gets the base64 String from wet-signature @Output
    this.authData.get('output').setValue(sig);
  }

  sigCheck(sigCheck: boolean) {
    this.sigCheckValue = sigCheck;
  }

  setDate() {
    if (this.fcraLegalRequirementsMet.value) {
      if (!this.date) {
        if (!this.authorizationUser.fcraLegalRequirementCheckDate) {
          this.date = this.datepipe.transform(new Date(), 'MM/dd/yyyy HH:mm:ss');
        }
      }
    } else if (!this.authorizationUser.fcraLegalRequirementCheckDate) {
      this.date = null;
    }
  }

  authorizeOnBlur() {
    if (this.authorizeConsent.value) {
      this.check = false;
    } else {
      this.check = true;
    }
  }

  authorizeOnFocus() {
    this.check = false;
  }

  legalOnBlur() {
    if (this.fcraLegalRequirementsMet.value) {
      this.checkTwo = false;
    } else {
      this.checkTwo = true;
    }
  }

  legalOnFocus() {
    this.checkTwo = false;
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  validate() {
    this.authData.markAllAsTouched();
    this.userService.setFocus(this.authData);
    if (!this.authorizeConsent.value) {
      this.authorizeConsent.setErrors({ invalid: true });
      this.check = true;
    }

    if (!this.fcraLegalRequirementsMet.value) {
      this.fcraLegalRequirementsMet.setErrors({ invalid: true });
      this.checkTwo = true;
    }

    if (!this.output.value) {
      this.output.setErrors({ invalid: true });
    }

    if (this.authData.invalid) {
      return false;
    }

    return true;
  }

}
